/**
 * ModalQueueContext & Provider
 *
 * Manage a queue of modals in React: shows one modal at a time, queues the rest.
 * Use <ModalQueueProvider> and the useModalQueue hook.
 */
import {
  createContext, useContext, useState, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';

const ModalQueueContext = createContext();

export const ModalQueueProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);

  /**
   * Processes the modal queue: if no modal is shown and queue is not empty, shows the next modal.
   */
  const processQueue = useCallback(() => {
    if (!current && queue.length > 0) {
      const [next, ...rest] = queue;
      setCurrent(next);
      setQueue(rest);
    }
  }, [current, queue]);

  /**
   * Adds a modal to the queue. If no modal is currently shown, shows it immediately.
   * @param {object} component - React element to show as modal (should have a 'component' prop).
   * @param {boolean} toStart - If true, adds modal to the front of the queue.
   */
  const showModal = useCallback((component, toStart = false) => {
    setQueue((prevQueue) => {
      const newQueue = toStart
        ? [component, ...prevQueue]
        : [...prevQueue, component];

      if (!current) {
        const [first, ...rest] = newQueue;
        setCurrent(first);
        return rest;
      }

      return newQueue;
    });
  }, [current]);

  /**
   * Closes the current modal. If there are more modals in the queue, shows the next one.
   */
  const closeModal = useCallback(() => {
    setCurrent(null);
  }, []);

  useEffect(() => {
    processQueue();
  }, [current, processQueue]);

  return (
    <ModalQueueContext.Provider value={{ showModal, closeModal }}>
      {children}
      {current?.component}
    </ModalQueueContext.Provider>
  );
};

export const useModalQueue = () => useContext(ModalQueueContext);

ModalQueueProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
